import { type CSSProperties, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { isScan, isViewableNonScan } from '@witmetrics/utils/files';
import { useDispatch } from '@witmetrics/data-store';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { downloadFileVersion, ensurePreview } from '@/utils/files';
import { useLoadedFiles } from '@/providers/LoadedFilesProvider';
import { showErrorToaster } from '@/utils/toasters';
import { loadScan } from '@/utils/scans';
import SceneProvider from '@/providers/SceneProvider';
import ViewerContent from './ViewerContent';
import { classes, fetchFileData } from './utils';

export type ScanServiceViewerProps = {
  isFetchingData?: boolean;
  className?: string;
  style?: CSSProperties;
  openFileIds: number[];
};

export default function ScanServiceViewer({
  isFetchingData,
  className,
  style,
  openFileIds,
}: ScanServiceViewerProps) {
  const currentFileIdsRef = useRef<number[]>([]);
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const { loadedFiles, addLoadedFile } = useLoadedFiles();
  const [isLoadingFiles, toggleLoadingFiles] = useToggle();

  useEffect(() => {
    if (openFileIds !== currentFileIdsRef.current) {
      handleLoadFiles();
    }
  }, [openFileIds]);

  const handleLoadFiles = async () => {
    try {
      // TODO: Optimize this to load in parallel
      for (const fileId of openFileIds) {
        const { file, fileVersion, fileNotes } = await fetchFileData(fileId);
        if (!fileVersion) {
          showErrorToaster('File version not found');
          continue;
        }
        dispatch.addFiles([file]);
        dispatch.addFileVersions([fileVersion]);
        dispatch.addFileNotes(fileNotes);
        if (!!loadedFiles[fileId]) continue; // File already loaded
        toggleLoadingFiles(true);
        const arrayBuffer = await downloadFileVersion(fileVersion.id);
        if (isScan(fileVersion.name)) {
          const mesh = loadScan(fileVersion.name, arrayBuffer, file.fileTypeId);
          if (!mesh) {
            showErrorToaster('Unrecognized scan format');
            continue;
          }
          mesh.userData.fileId = fileId;
          mesh.userData.fileTypeId = file.fileTypeId;
          addLoadedFile(fileId, mesh);
        } else if (isViewableNonScan(fileVersion.name)) {
          addLoadedFile(fileId, URL.createObjectURL(new Blob([arrayBuffer])));
        }
        const preview = await ensurePreview(
          fileVersion,
          file.fileTypeId,
          arrayBuffer
        );
        if (preview) {
          dispatch.addFileVersions([preview.updatedVersion]);
          addLoadedFile(
            `preview-${fileVersion.id}`,
            URL.createObjectURL(preview.preview)
          );
        }
      }
      currentFileIdsRef.current = openFileIds;
      cancelFetching();
    } catch (err) {
      cancelFetching();
      onApiError(err, 'Error loading file(s)');
    }
  };

  const cancelFetching = (message?: string) => {
    toggleLoadingFiles(false);
    if (message) return showErrorToaster(message);
  };

  return (
    <>
      <div style={style} className={twMerge(classes.wrapper, className)}>
        <SceneProvider>
          <ViewerContent
            isLoading={isFetchingData || isLoadingFiles}
            openFileIds={openFileIds}
          />
        </SceneProvider>
      </div>
    </>
  );
}
